/**
 * Enhanced API client with comprehensive error handling and retry logic
 */

import { parseError, logError } from './errorHandler';
import { retryApiCall, circuitBreakers } from './retryHandler';

// API configuration
const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api',
  timeout: 30000, // 30 seconds
  retries: 3,
  retryDelay: 1000
};

/**
 * Enhanced fetch wrapper with error handling and retry logic
 */
class ApiClient {
  constructor(config = {}) {
    this.config = { ...API_CONFIG, ...config };
    this.interceptors = {
      request: [],
      response: []
    };
    
    // Add default request interceptor for auth
    this.addRequestInterceptor((config) => {
      const tokens = localStorage.getItem('tokens');
      if (tokens) {
        try {
          const parsedTokens = JSON.parse(tokens);
          if (parsedTokens.access_token) {
            config.headers = {
              ...config.headers,
              'Authorization': `Bearer ${parsedTokens.access_token}`
            };
          }
        } catch (error) {
          console.warn('Failed to parse auth tokens:', error);
        }
      }
      return config;
    });
    
    // Add default response interceptor for error handling
    this.addResponseInterceptor(
      (response) => response,
      (error) => this.handleResponseError(error)
    );
  }
  
  /**
   * Add request interceptor
   */
  addRequestInterceptor(onFulfilled, onRejected) {
    this.interceptors.request.push({ onFulfilled, onRejected });
  }
  
  /**
   * Add response interceptor
   */
  addResponseInterceptor(onFulfilled, onRejected) {
    this.interceptors.response.push({ onFulfilled, onRejected });
  }
  
  /**
   * Apply request interceptors
   */
  async applyRequestInterceptors(config) {
    let processedConfig = config;
    
    for (const interceptor of this.interceptors.request) {
      try {
        if (interceptor.onFulfilled) {
          processedConfig = await interceptor.onFulfilled(processedConfig);
        }
      } catch (error) {
        if (interceptor.onRejected) {
          processedConfig = await interceptor.onRejected(error);
        } else {
          throw error;
        }
      }
    }
    
    return processedConfig;
  }
  
  /**
   * Apply response interceptors
   */
  async applyResponseInterceptors(response, error = null) {
    let processedResponse = response;
    let processedError = error;
    
    for (const interceptor of this.interceptors.response) {
      try {
        if (processedError && interceptor.onRejected) {
          processedResponse = await interceptor.onRejected(processedError);
          processedError = null;
        } else if (processedResponse && interceptor.onFulfilled) {
          processedResponse = await interceptor.onFulfilled(processedResponse);
        }
      } catch (err) {
        processedError = err;
        processedResponse = null;
      }
    }
    
    if (processedError) {
      throw processedError;
    }
    
    return processedResponse;
  }
  
  /**
   * Handle response errors
   */
  async handleResponseError(error) {
    const parsedError = parseError(error);
    
    // Handle token expiration
    if (parsedError.code === 'TOKEN_EXPIRED' || parsedError.code === 'INVALID_TOKEN') {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    // Log error for debugging
    logError(error, {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status
    });
    
    throw parsedError;
  }
  
  /**
   * Build full URL
   */
  buildUrl(endpoint) {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    return `${this.config.baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  }
  
  /**
   * Create request configuration
   */
  createRequestConfig(method, endpoint, options = {}) {
    const config = {
      method: method.toUpperCase(),
      url: this.buildUrl(endpoint),
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      timeout: options.timeout || this.config.timeout,
      ...options
    };
    
    // Add body for POST/PUT/PATCH requests
    if (['POST', 'PUT', 'PATCH'].includes(config.method) && options.data) {
      if (config.headers['Content-Type'] === 'application/json') {
        config.body = JSON.stringify(options.data);
      } else {
        config.body = options.data;
      }
    }
    
    return config;
  }
  
  /**
   * Execute request with retry logic and circuit breaker
   */
  async executeRequest(config) {
    // Apply request interceptors
    const processedConfig = await this.applyRequestInterceptors(config);
    
    // Use circuit breaker for external APIs
    const circuitBreaker = this.getCircuitBreaker(processedConfig.url);
    
    const requestFn = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), processedConfig.timeout);
      
      try {
        const response = await fetch(processedConfig.url, {
          ...processedConfig,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Check if response is ok
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.message || `HTTP ${response.status}`);
          error.response = { status: response.status, data: errorData };
          error.config = processedConfig;
          throw error;
        }
        
        // Parse response
        const data = await response.json();
        const processedResponse = await this.applyResponseInterceptors({ data, status: response.status });
        
        return processedResponse.data;
      } catch (error) {
        clearTimeout(timeoutId);
        
        // Handle abort error
        if (error.name === 'AbortError') {
          const timeoutError = new Error('Request timeout');
          timeoutError.code = 'TIMEOUT_ERROR';
          timeoutError.config = processedConfig;
          throw timeoutError;
        }
        
        // Handle network error
        if (!error.response) {
          const networkError = new Error('Network error');
          networkError.code = 'NETWORK_ERROR';
          networkError.config = processedConfig;
          throw networkError;
        }
        
        error.config = processedConfig;
        throw error;
      }
    };
    
    // Execute with circuit breaker if available
    if (circuitBreaker) {
      return circuitBreaker.execute(() => 
        retryApiCall(requestFn, {
          endpoint: processedConfig.url,
          method: processedConfig.method
        })
      );
    }
    
    // Execute with retry logic
    return retryApiCall(requestFn, {
      endpoint: processedConfig.url,
      method: processedConfig.method
    });
  }
  
  /**
   * Get appropriate circuit breaker for URL
   */
  getCircuitBreaker(url) {
    if (url.includes('jup.ag') || url.includes('jupiter')) {
      return circuitBreakers.jupiter;
    }
    if (url.includes('dexscreener') || url.includes('price')) {
      return circuitBreakers.priceApi;
    }
    if (url.includes('solana') || url.includes('rpc')) {
      return circuitBreakers.blockchain;
    }
    return null;
  }
  
  /**
   * HTTP methods
   */
  async get(endpoint, options = {}) {
    const config = this.createRequestConfig('GET', endpoint, options);
    return this.executeRequest(config);
  }
  
  async post(endpoint, data = null, options = {}) {
    const config = this.createRequestConfig('POST', endpoint, { ...options, data });
    return this.executeRequest(config);
  }
  
  async put(endpoint, data = null, options = {}) {
    const config = this.createRequestConfig('PUT', endpoint, { ...options, data });
    return this.executeRequest(config);
  }
  
  async patch(endpoint, data = null, options = {}) {
    const config = this.createRequestConfig('PATCH', endpoint, { ...options, data });
    return this.executeRequest(config);
  }
  
  async delete(endpoint, options = {}) {
    const config = this.createRequestConfig('DELETE', endpoint, options);
    return this.executeRequest(config);
  }
  
  /**
   * Upload file
   */
  async upload(endpoint, file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional fields
    if (options.fields) {
      Object.entries(options.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    
    const config = this.createRequestConfig('POST', endpoint, {
      ...options,
      data: formData,
      headers: {
        // Don't set Content-Type, let browser set it with boundary
        ...options.headers
      }
    });
    
    delete config.headers['Content-Type'];
    
    return this.executeRequest(config);
  }
  
  /**
   * Download file
   */
  async download(endpoint, options = {}) {
    const config = this.createRequestConfig('GET', endpoint, {
      ...options,
      headers: {
        'Accept': 'application/octet-stream',
        ...options.headers
      }
    });
    
    const processedConfig = await this.applyRequestInterceptors(config);
    
    const response = await fetch(processedConfig.url, processedConfig);
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    
    return response.blob();
  }
}

// Create default API client instance
export const apiClient = new ApiClient();

// Convenience methods for common API patterns
export const api = {
  // Authentication
  auth: {
    login: (credentials) => apiClient.post('/auth/login', credentials),
    register: (userData) => apiClient.post('/auth/register', userData),
    logout: () => apiClient.post('/auth/logout'),
    refresh: () => apiClient.post('/auth/refresh'),
    me: () => apiClient.get('/auth/me')
  },
  
  // Wallet operations
  wallets: {
    list: () => apiClient.get('/wallets'),
    create: (walletData) => apiClient.post('/wallets', walletData),
    connect: (walletData) => apiClient.post('/wallets/connect', walletData),
    delete: (walletId) => apiClient.delete(`/wallets/${walletId}`),
    balance: (walletId) => apiClient.get(`/wallets/${walletId}/balance`)
  },
  
  // Token operations
  tokens: {
    search: (query) => apiClient.get(`/tokens/search?q=${encodeURIComponent(query)}`),
    info: (mint) => apiClient.get(`/tokens/${mint}/info`),
    price: (mint) => apiClient.get(`/tokens/${mint}/price`),
    history: (mint, timeframe) => apiClient.get(`/tokens/${mint}/history?timeframe=${timeframe}`)
  },
  
  // Trading operations
  trading: {
    start: (tradeData) => apiClient.post('/trading/start', tradeData),
    stop: (tradeId) => apiClient.post(`/trading/${tradeId}/stop`),
    status: (tradeId) => apiClient.get(`/trading/${tradeId}/status`),
    history: (params = {}) => apiClient.get('/trading/history', { params }),
    quote: (quoteData) => apiClient.post('/trading/quote', quoteData),
    execute: (tradeData) => apiClient.post('/trading/execute', tradeData)
  },
  
  // Assets
  assets: {
    list: () => apiClient.get('/assets'),
    portfolio: () => apiClient.get('/assets/portfolio')
  }
};

export default apiClient;